import type { InternalNotificationSchema, NotificationType } from '@buyerflo/core/schemas/notification'
import { Bell, Check, ClipboardCheck, ClipboardList } from '@tamagui/lucide-icons-2'
import { useNotificationsApi } from 'apiProviders'
import Button from 'components/button'
import { type NotificationBucket, useFormatNotificationTime, useNotificationBucket } from 'notificationTime'
import { Fragment, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { Popover, ScrollView, SizableText, Spinner, Text, View, XStack, YStack } from 'tamagui'

/**
 * Per-type icon + colour for the notification row glyph. Keyed by the stored
 * `type`; notifications without a known type (automation, legacy rows) fall back
 * to `DEFAULT_TYPE_META`. The `soft` token is the translucent tint behind the icon.
 */
const TYPE_META: Record<NotificationType, { Icon: typeof Bell; color: string; soft: string }> = {
    formCompleted: { Icon: ClipboardCheck, color: '$statusOk', soft: '$statusOkSoft' },
    wishlistSubmitted: { Icon: ClipboardList, color: '$statusInfo', soft: '$statusInfoSoft' },
}
const DEFAULT_TYPE_META = { Icon: Bell, color: '$statusViolet', soft: '$statusVioletSoft' }

/** Bell trigger that opens the notifications dropdown, replacing the dedicated page. */
export default function NotificationsDropdown({ active }: { active?: boolean }) {
    const navigate = useNavigate()
    const notificationsApi = useNotificationsApi()
    const formatNotificationTime = useFormatNotificationTime()
    const bucketOf = useNotificationBucket()

    const [open, setOpen] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)

    const notifications = notificationsApi.data ?? []
    const unseenCount = notifications.filter(n => !n.seen).length
    const unreadCount = notifications.filter(n => !n.read).length

    // Opening the dropdown marks notifications as seen (synced server-side), mirroring the old page's on-mount behaviour.
    // The bell badge tracks unread (not seen), so it persists until notifications are actually read.
    useEffect(() => {
        if (open && notifications.length > 0 && unseenCount > 0) {
            notificationsApi.setAllSeen()
        }
    }, [open, notifications.length, unseenCount])

    // Group into recency buckets. Notifications arrive newest-first (the query
    // uses ScanIndexForward: false), so buckets are contiguous and already in
    // Today → Earlier order — comparing against the last group dedupes headers.
    const groups: { label: NotificationBucket; items: InternalNotificationSchema[] }[] = []
    for (const n of notifications) {
        const label = bucketOf(n.notificationTime)
        const last = groups[groups.length - 1]
        if (last && last.label === label) last.items.push(n)
        else groups.push({ label, items: [n] })
    }

    return (
        <Popover
            open={open}
            onOpenChange={setOpen}
            placement="bottom-end"
            allowFlip
            offset={8}
        >
            <Popover.Trigger asChild>
                <View
                    position="relative"
                    width={32}
                    height={32}
                    borderRadius="$3"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer"
                    animation="quick"
                    borderWidth={1}
                    borderColor={active || open ? 'transparent' : '$borderColor'}
                    backgroundColor={active || open ? '$accentSoft' : 'transparent'}
                    hoverStyle={{ backgroundColor: active || open ? '$accentSoft' : '$backgroundHover' }}
                    pressStyle={{ opacity: 0.85 }}
                >
                    <Bell
                        size={18}
                        color={active || open ? '$accentText' : '$colorSoft'}
                    />
                    {unreadCount > 0 && (
                        <View
                            position="absolute"
                            top={-2}
                            right={-2}
                            width={10}
                            height={10}
                            borderRadius={999}
                            backgroundColor="$statusDanger"
                            borderWidth={2}
                            borderColor="$backgroundStrong"
                        />
                    )}
                </View>
            </Popover.Trigger>

            <Popover.Content
                padding={0}
                width={392}
                maxWidth="calc(100vw - 24px)"
                borderWidth={1}
                borderColor="$borderColor"
                backgroundColor="$backgroundStrong"
                borderRadius="$5"
                overflow="hidden"
                elevate
                enterStyle={{ y: -6, opacity: 0 }}
                exitStyle={{ y: -6, opacity: 0 }}
                animation={[
                    'quick',
                    {
                        opacity: { overshootClamping: true },
                    },
                ]}
            >
                {/* Header */}
                <XStack
                    width="100%"
                    alignItems="center"
                    justifyContent="space-between"
                    gap="$3"
                    paddingVertical="$3"
                    paddingHorizontal="$3.5"
                    borderBottomWidth={1}
                    borderBottomColor="$borderColor"
                >
                    <XStack
                        alignItems="center"
                        gap="$2"
                    >
                        <SizableText
                            size="$3"
                            fontWeight="600"
                            color="$color"
                        >
                            Notifications
                        </SizableText>
                        {unreadCount > 0 && (
                            <XStack
                                alignItems="center"
                                gap="$1.5"
                                backgroundColor="$statusInfoSoft"
                                borderRadius={999}
                                paddingHorizontal="$2"
                                height={20}
                            >
                                <View
                                    width={6}
                                    height={6}
                                    borderRadius={3}
                                    backgroundColor="$statusInfo"
                                />
                                <Text
                                    fontSize={11}
                                    fontWeight="600"
                                    color="$statusInfo"
                                >
                                    {unreadCount} new
                                </Text>
                            </XStack>
                        )}
                    </XStack>
                    <Button
                        size="$2"
                        chromeless
                        disabled={unreadCount === 0}
                        opacity={unreadCount === 0 ? 0.4 : 1}
                        onPress={() => notificationsApi.markAllRead()}
                        paddingHorizontal="$2"
                        pressStyle={{ opacity: 0.6 }}
                    >
                        <Check
                            size={12}
                            color="$colorSoft"
                        />
                        <SizableText
                            size="$2"
                            color="$colorSoft"
                            fontWeight="500"
                        >
                            Mark all read
                        </SizableText>
                    </Button>
                </XStack>

                {/* List */}
                {notificationsApi.data == undefined ? (
                    <View
                        paddingVertical="$6"
                        alignItems="center"
                    >
                        <Spinner
                            size="small"
                            color="$primary"
                        />
                    </View>
                ) : notifications.length === 0 ? (
                    <YStack
                        alignItems="center"
                        justifyContent="center"
                        paddingVertical="$8"
                        paddingHorizontal="$6"
                        gap="$2"
                    >
                        <View
                            width={44}
                            height={44}
                            borderRadius="$4"
                            backgroundColor="$backgroundHover"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Bell
                                size={20}
                                color="$colorMuted"
                            />
                        </View>
                        <SizableText
                            size="$3"
                            fontWeight="500"
                            color="$color"
                        >
                            You're all caught up
                        </SizableText>
                        <SizableText
                            size="$2"
                            color="$colorMuted"
                            textAlign="center"
                        >
                            You'll be notified when clients sign forms, when documents are ready, and more.
                        </SizableText>
                    </YStack>
                ) : (
                    <ScrollView
                        maxHeight={440}
                        // @ts-ignore - hide native scrollbar so item dividers align with the header divider
                        style={{
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                        }}
                    >
                        {groups.map(group => (
                            <Fragment key={group.label}>
                                <View
                                    width="100%"
                                    paddingTop="$3"
                                    paddingBottom="$1.5"
                                    paddingHorizontal="$3.5"
                                    backgroundColor="$backgroundStrong"
                                    borderBottomWidth={1}
                                    borderBottomColor="$borderColor"
                                    // @ts-ignore - sticky is web-only; keeps the bucket label pinned while its rows scroll under it
                                    style={{ position: 'sticky', top: 0, zIndex: 1 }}
                                >
                                    <SizableText
                                        fontSize={11}
                                        fontWeight="600"
                                        color="$colorMuted"
                                        textTransform="uppercase"
                                        letterSpacing={0.6}
                                    >
                                        {group.label}
                                    </SizableText>
                                </View>
                                {group.items.map(notification => {
                                    const meta =
                                        (notification.type && TYPE_META[notification.type]) ||
                                        DEFAULT_TYPE_META
                                    const TypeIcon = meta.Icon
                                    return (
                                        <XStack
                                            key={notification.notificationId}
                                            width="100%"
                                            gap="$2.5"
                                            alignItems="flex-start"
                                            paddingVertical="$3"
                                            paddingHorizontal="$3.5"
                                            borderBottomWidth={1}
                                            borderBottomColor="$borderColor"
                                            cursor="pointer"
                                            animation="quick"
                                            hoverStyle={{ backgroundColor: '$backgroundHover' }}
                                            onPress={() => {
                                                if (!notification.read) {
                                                    notificationsApi.markAsRead(notification.notificationId)
                                                }
                                                if (notification.link) {
                                                    setOpen(false)
                                                    navigate(notification.link)
                                                }
                                            }}
                                        >
                                            <View
                                                width={30}
                                                height={30}
                                                borderRadius="$3"
                                                flexShrink={0}
                                                alignItems="center"
                                                justifyContent="center"
                                                backgroundColor={meta.soft}
                                            >
                                                <TypeIcon
                                                    size={15}
                                                    color={meta.color}
                                                />
                                            </View>
                                            <YStack
                                                flex={1}
                                                minWidth={0}
                                                gap="$1"
                                            >
                                                <SizableText
                                                    size="$2"
                                                    lineHeight={18}
                                                    color="$color"
                                                    fontWeight={notification.read ? '400' : '500'}
                                                >
                                                    {notification.message}
                                                </SizableText>
                                                <SizableText
                                                    fontSize={11}
                                                    color="$colorMuted"
                                                >
                                                    {formatNotificationTime(notification.notificationTime)}
                                                </SizableText>
                                            </YStack>
                                            {!notification.read && (
                                                <View
                                                    width={7}
                                                    height={7}
                                                    borderRadius={4}
                                                    alignSelf="center"
                                                    flexShrink={0}
                                                    backgroundColor="$primary"
                                                />
                                            )}
                                        </XStack>
                                    )
                                })}
                            </Fragment>
                        ))}

                        {!notificationsApi.lastPage &&
                            (loadingMore ? (
                                <View
                                    paddingVertical="$3"
                                    alignItems="center"
                                >
                                    <Spinner
                                        size="small"
                                        color="$primary"
                                    />
                                </View>
                            ) : (
                                <XStack
                                    paddingVertical="$3"
                                    justifyContent="center"
                                    cursor="pointer"
                                    hoverStyle={{ opacity: 0.8 }}
                                    onPress={async () => {
                                        setLoadingMore(true)
                                        await notificationsApi.nextPage()
                                        setLoadingMore(false)
                                    }}
                                >
                                    <SizableText
                                        size="$2"
                                        color="$primary"
                                        fontWeight="600"
                                    >
                                        Load more
                                    </SizableText>
                                </XStack>
                            ))}
                    </ScrollView>
                )}
            </Popover.Content>
        </Popover>
    )
}
